import React from 'react';
import {
  Sun,
  Moon,
  Shield,
  User as UserIcon,
  RefreshCw,
  Building2,
  Database,
  Cloud,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useReconciliation } from '../../context/ReconciliationContext';

export const Navbar: React.FC = () => {
  const { user, isAdmin, switchRole } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { isReconciled, summary, activeRunId } = useReconciliation();

  return (
    <header className="sticky top-0 z-30 bg-white/95 dark:bg-slate-900/95 backdrop-blur border-b border-slate-200 dark:border-slate-800 transition-colors">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Brand & App Title */}
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-tr from-brand-700 via-brand-600 to-sky-400 flex items-center justify-center text-white shadow-md shadow-brand-500/20">
              <Building2 className="h-5 w-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-bold text-lg text-slate-900 dark:text-white tracking-tight">
                  SchemePay
                </span>
                <span className="text-xs px-2 py-0.5 rounded bg-brand-50 text-brand-700 dark:bg-brand-950 dark:text-brand-300 font-semibold border border-brand-200 dark:border-brand-800">
                  Recon Hub
                </span>
                <span className="hidden sm:inline-flex items-center gap-1 text-[11px] text-emerald-600 dark:text-emerald-400 font-mono bg-emerald-50 dark:bg-emerald-950/40 px-1.5 py-0.5 rounded border border-emerald-200 dark:border-emerald-900">
                  <Cloud className="w-3 h-3" /> Cloudflare Edge
                </span>
              </div>
              <p className="text-[11px] text-slate-500 dark:text-slate-400 hidden sm:block">
                Razorpay &times; Multi-Bill ERP Automated Reconciliation Engine
              </p>
            </div>
          </div>

          {/* Center: Live Reconciliation Status */}
          {isReconciled && summary && (
            <div className="hidden md:flex items-center gap-3 bg-slate-100 dark:bg-slate-800/80 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 text-xs">
              <span className="text-slate-500 dark:text-slate-400 font-medium">
                Active Batch:
              </span>
              <span className="font-mono font-semibold text-slate-800 dark:text-slate-200">
                {activeRunId || 'LIVE_BATCH'}
              </span>
              <span className="text-slate-300 dark:text-slate-600">|</span>
              <span className="text-emerald-600 dark:text-emerald-400 font-semibold flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-emerald-500" />
                {summary.matchPercentage}% Matched
              </span>
              <span className="text-slate-300 dark:text-slate-600">|</span>
              <span className="text-slate-600 dark:text-slate-300">
                ₹{summary.totalCollectionAmount.toLocaleString('en-IN')}
              </span>
            </div>
          )}

          {/* Right Controls */}
          <div className="flex items-center space-x-3">
            {/* Quick Role Switcher (Finance User vs Admin) */}
            <div className="flex items-center bg-slate-100 dark:bg-slate-800 p-0.5 rounded-lg border border-slate-200 dark:border-slate-700 text-xs">
              <button
                onClick={() => switchRole('finance_user')}
                className={`px-2.5 py-1 rounded-md transition font-medium flex items-center gap-1.5 ${
                  !isAdmin
                    ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                }`}
                title="Switch to Finance User view"
              >
                <UserIcon className="w-3.5 h-3.5" />
                Finance User
              </button>
              <button
                onClick={() => switchRole('admin')}
                className={`px-2.5 py-1 rounded-md transition font-medium flex items-center gap-1.5 ${
                  isAdmin
                    ? 'bg-white dark:bg-slate-700 text-brand-700 dark:text-brand-300 shadow-sm'
                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                }`}
                title="Switch to Admin view (Audit, User mgmt, Rules)"
              >
                <Shield className="w-3.5 h-3.5 text-brand-600 dark:text-brand-400" />
                Admin
              </button>
            </div>

            {/* Dark Mode Toggle */}
            <button
              onClick={toggleTheme}
              aria-label="Toggle theme"
              className="p-2 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white bg-slate-100 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 transition"
            >
              {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>

            {/* Active User Avatar */}
            <div className="flex items-center gap-2 pl-2 border-l border-slate-200 dark:border-slate-700">
              <div className="w-8 h-8 rounded-full bg-brand-600 text-white flex items-center justify-center font-bold text-xs shadow-sm">
                {user?.name ? user.name.charAt(0) : 'U'}
              </div>
              <div className="hidden lg:block text-left">
                <div className="text-xs font-semibold text-slate-800 dark:text-slate-200">
                  {user?.name}
                </div>
                <div className="text-[10px] text-slate-500 dark:text-slate-400">
                  {user?.department}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};
